import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import {
  addTestimonial,
  resetTestimonialState,
} from "../../store/common/testimonialSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { Helmet } from "react-helmet-async";

/* ---------------- Animations ---------------- */

const container = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: "easeOut", staggerChildren: 0.12 },
  },
};

const field = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

/* ---------------- Thank You Modal ---------------- */

const ThankYouModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md"
        >
          <motion.div
            initial={{ scale: 0.85, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0, y: 40 }}
            transition={{ type: "spring", stiffness: 120, damping: 18 }}
            className="
              relative w-[90%] max-w-md rounded-3xl
              bg-gradient-to-br from-[#0a0d12] via-[#07090c] to-black
              border border-white/10
              p-8 text-center
              shadow-[0_40px_120px_rgba(0,0,0,0.9)]
            "
          >
            {/* Glow */}
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-48 w-48 rounded-full bg-[#2fa9bf]/25 blur-[130px]" />

            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.15, type: "spring", stiffness: 220 }}
              className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#2fa9bf]/20"
            >
              <span className="text-3xl">💎</span>
            </motion.div>

            <h3 className="text-2xl font-semibold text-white">Thank You!</h3>

            <p className="mt-2 text-sm leading-relaxed text-gray-400">
              We truly appreciate you taking the time to share your experience.
              Your smile inspires us ✨
            </p>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="
                mt-6 w-full rounded-xl
                bg-gradient-to-r from-[#2fa9bf] to-cyan-400
                py-3 font-semibold text-black
                shadow-[0_20px_60px_rgba(47,169,191,0.45)]
              "
            >
              Close
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ---------------- Main Form ---------------- */

const TestimonialForm = () => {
  const dispatch = useAppDispatch();
  const { loading, success, error } = useAppSelector(
    (state) => state.testimonial,
  );

  const [author, setAuthor] = useState("");
  const [region, setRegion] = useState("");
  const [review, setReview] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(addTestimonial({ author, region, review }));
  };

  useEffect(() => {
    if (success) {
      setAuthor("");
      setRegion("");
      setReview("");
    }
  }, [success]);

  return (
    <>
      <Helmet>
        <title>Feedbacks</title>
      </Helmet>
      <div className="relative min-h-screen bg-[#050608] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute left-0 top-8 w-full h-full scale-125 rotate-12">
          <img
            src="/Home/wave2.svg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        {/* Ambient glow */}
        <div className="absolute -top-48 -left-48 h-[600px] w-[600px] rounded-full bg-[#2fa9bf]/20 blur-[220px]" />
        <div className="absolute bottom-0 right-0 h-[500px] w-[500px] rounded-full bg-cyan-500/10 blur-[200px]" />

        <motion.div
          variants={container}
          initial="hidden"
          animate="visible"
          className="
          relative w-full max-w-xl rounded-3xl
          bg-black/40 backdrop-blur-2xl
          border border-white/10
          shadow-[0_60px_140px_rgba(0,0,0,0.95)]
          p-6 sm:p-8 md:p-10
        "
        >
          {/* Reflection */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-30 pointer-events-none" />

          <motion.div
            variants={field}
            className="mb-10 text-center relative z-10"
          >
            <h2 className="text-3xl font-semibold tracking-wide text-white">
              Patient Testimonial
            </h2>
            <p className="mt-2 text-sm text-gray-400">
              Your smile, your story ✨
            </p>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
            {/* Name */}
            <motion.div variants={field}>
              <label className="block mb-2 text-xs font-medium text-gray-400">
                Name
              </label>
              <input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                required
                className="
                w-full rounded-xl
                bg-gradient-to-br from-[#0a0d12] via-[#07090c] to-black
                border border-white/10
                px-4 py-3 text-white
                outline-none
                focus:border-[#2fa9bf]
                focus:ring-2 focus:ring-[#2fa9bf]/30
              "
              />
            </motion.div>

            {/* Region */}
            <motion.div variants={field}>
              <label className="block mb-2 text-xs font-medium text-gray-400">
                Region
              </label>
              <input
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                required
                className="
                w-full rounded-xl
                bg-gradient-to-br from-[#0a0d12] via-[#07090c] to-black
                border border-white/10
                px-4 py-3 text-white
                outline-none
                focus:border-[#2fa9bf]
                focus:ring-2 focus:ring-[#2fa9bf]/30
              "
              />
            </motion.div>

            {/* Message */}
            <motion.div variants={field}>
              <label className="block mb-2 text-xs font-medium text-gray-400">
                Message
              </label>
              <textarea
                value={review}
                onChange={(e) => setReview(e.target.value)}
                rows={4}
                required
                className="
                w-full resize-none rounded-xl
                bg-gradient-to-br from-[#0a0d12] via-[#07090c] to-black
                border border-white/10
                px-4 py-3 text-white
                outline-none
                focus:border-[#2fa9bf]
                focus:ring-2 focus:ring-[#2fa9bf]/30
              "
              />
            </motion.div>

            {error && (
              <p className="text-sm text-red-400 text-center">{error}</p>
            )}

            <motion.button
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.05 }}
              whileTap={{ scale: loading ? 1 : 0.96 }}
              className="
              relative w-full overflow-hidden rounded-xl
              bg-gradient-to-r from-[#2fa9bf] to-cyan-400
              py-3 font-semibold text-black
              shadow-[0_20px_60px_rgba(47,169,191,0.45)]
              disabled:opacity-60
            "
            >
              {loading ? "Submitting..." : "Submit Testimonial"}
            </motion.button>
          </form>
        </motion.div>

        {/* Success Modal */}
        <ThankYouModal
          open={success}
          onClose={() => dispatch(resetTestimonialState())}
        />
      </div>
    </>
  );
};

export default TestimonialForm;
